import { useRef, useEffect, useState } from "react";
import _, { DebouncedFunc } from "lodash";

export const useDebounce = <T>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return debouncedValue;
};

/**
 * Returns a new function representing the debouncing wrapper for `fn`.
 * @deprecated: useDebounced should be used as a more stable version.
 * NOTE: the resulting function does not depend on the hook parameters, thus it is not recreated on their change.
 * This might lead to "unexpected" effects when the `fn` changes on succeeding calls but gets never called
 * because the firstly created instance is only used.
 * As a solution to this case, provided it is not fixable otherwise, is to add the conventional parameter
 * `deps: any[] = []` which would be tracked for mutations to recreate the debounced function.
 */
export const useDebounceFunction = <F extends AnyFunction>(fn: F, delay: number) => {
    const debounced = useRef(_.debounce(fn, delay)).current;
    useEffect(() => debounced.cancel, [debounced]);
    return debounced;
};

/**
 * Returns a new function representing the debouncing wrapper for `fn`.
 * @param {Function} fn - function to call by debounce
 * @param {number} [delay=100] delay in milliseconds.
 * @returns debounced function.
 */
export const useDebounced = <F extends AnyFunction>(fn: F, delay: number = 100): DebouncedFunc<F> => {
    const debounced = useRef(_.debounce(fn, delay));
    useEffect(() => {
        debounced.current = _.debounce(fn, delay);
        return () => debounced.current?.cancel();
    }, [fn, delay]);
    return debounced.current;
};
